<template>
  <div class="franquias-operacional-tab">
    <!-- Card Principal - Configurações Operacionais -->
    <div class="main-card">
      <div class="card-header">
        <div class="header-icon">
          <i class="pi pi-cog"></i>
        </div>
        <div class="header-content">
          <h3>{{ t('Configurações Operacionais') }}</h3>
          <p>{{ t('Defina a capacidade e metas produtivas da franquia') }}</p>
        </div>
      </div>

      <div class="operacional-grid">
        <!-- Card Capacidade -->
        <div class="operational-card primary">
          <div class="card-icon">
            <i class="pi pi-chart-bar"></i>
          </div>
          <div class="card-label">{{ t('Capacidade de Produção') }}</div>
          <div class="card-input">
            <input-number
              data-testid="franquias-operacional-capacidade-diaria"
              elem-id="capacidade_producao_diaria"
              :label="t('Capacidade Diária')"
              placeholder="0"
              :min="0"
              :suffix="' ' + t('itens/dia')"
              v-model="localFormData.capacidade_producao_diaria"
              @update:modelValue="handleInputChange"
            />
          </div>
        </div>

        <!-- Card Meta -->
        <div class="operational-card success">
          <div class="card-icon">
            <i class="pi pi-bullseye"></i>
          </div>
          <div class="card-label">{{ t('Meta de Produção') }}</div>
          <div class="card-input">
            <input-number
              data-testid="franquias-operacional-meta-diaria"
              elem-id="meta_producao_itens_dia"
              :label="t('Meta Diária')"
              placeholder="0"
              :min="0"
              :suffix="' ' + t('itens/dia')"
              v-model="localFormData.meta_producao_itens_dia"
              @update:modelValue="handleInputChange"
            />
          </div>
        </div>

        <!-- Card Ticket Médio -->
        <div class="operational-card warning full-width">
          <div class="card-icon">
            <i class="pi pi-tag"></i>
          </div>
          <div class="card-label">{{ t('Ticket Médio por Par') }}</div>
          <div class="card-input">
            <input-currency
              data-testid="franquias-operacional-valor-medio-par"
              elem-id="valor_medio_item"
              :label="t('Valor Médio')"
              :placeholder="t('R$ 0,00')"
              v-model="localFormData.valor_medio_item"
              @update:modelValue="handleInputChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Card de Estimativa -->
    <div class="estimate-card">
      <div class="estimate-content">
        <div class="estimate-icon">
          <i class="pi pi-calculator"></i>
        </div>
        <div class="estimate-info">
          <span class="estimate-label">{{ t('Estimativa de Faturamento Mensal') }}</span>
          <span class="estimate-value">{{ formatCurrency(metaMensal) }}</span>
        </div>
        <div class="estimate-hint">
          <small>{{ t('Baseado em 22 dias úteis') }}</small>
        </div>
      </div>
    </div>

    <!-- Card de Progresso -->
    <div class="progress-card">
      <div class="progress-header">
        <div class="progress-title">
          <i class="pi pi-chart-bar"></i>
          <span>{{ t('Utilização da Capacidade') }}</span>
        </div>
        <span class="progress-percent">{{ utilizationPercent }}%</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: utilizationPercent + '%' }"></div>
      </div>
      <div class="progress-hint">
        <small v-if="utilizationPercent < 50">{{ t('Abaixo da capacidade - há espaço para crescer') }}</small>
        <small v-else-if="utilizationPercent < 80">{{ t('Boa utilização da capacidade instalada') }}</small>
        <small v-else>{{ t('Capacidade quase atingida - considere expandir') }}</small>
      </div>
    </div>

    <!-- Ações -->
    <div class="actions-section">
      <Button
        data-testid="franquias-operacional-save-button"
        :label="t('Salvar Configurações')"
        icon="pi pi-save"
        severity="primary"
        size="large"
        :loading="isSaving"
        @click="handleSave"
      />
    </div>

    <!-- Info Card -->
    <div class="info-card">
      <i class="pi pi-lightbulb"></i>
      <div class="info-content">
        <strong>{{ t('Configurações Operacionais') }}</strong>
        <p>{{ t('Configure a capacidade produtiva da franquia para cálculos financeiros e projeções. A estimativa mensal é calculada com base na meta diária e no ticket médio, considerando 22 dias úteis por mês.') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useFranquias } from '../../composables/useFranquias';
import { log } from '../../utils/DebugLogger';

interface OperacionalForm {
  capacidade_producao_diaria: number | null;
  meta_producao_itens_dia: number | null;
  valor_medio_item: number | null;
}

interface ConfiguracaoResponse {
  Success?: boolean;
  Dados?: Partial<OperacionalForm>;
}

interface FranquiaData {
  [key: string]: unknown;
}

interface Props {
  franquiaId: number;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaData: null,
});

const { t } = useI18n();
const { fetchConfiguracaoFranquia, saveOperacional } = useFranquias();

// Estado local
const isSaving = ref<boolean>(false);
const localFormData = ref<OperacionalForm>({
  capacidade_producao_diaria: null,
  meta_producao_itens_dia: null,
  valor_medio_item: null,
});

// Estimativa mensal
const metaMensal = computed<number>(() => {
  const { meta_producao_itens_dia, valor_medio_item } = localFormData.value;
  if (meta_producao_itens_dia && valor_medio_item) {
    return meta_producao_itens_dia * 22 * valor_medio_item;
  }
  return 0;
});

// Percentual de utilização
const utilizationPercent = computed<number>(() => {
  const capacidade = localFormData.value.capacidade_producao_diaria || 0;
  const meta = localFormData.value.meta_producao_itens_dia || 0;

  if (capacidade > 0) {
    return Math.min(Math.round((meta / capacidade) * 100), 100);
  }
  return 0;
});

// Formatar moeda
const formatCurrency = (value: number | null | undefined): string => {
  if (!value) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Carregar configuração existente
const loadConfiguracao = async (): Promise<void> => {
  try {
    const config = await fetchConfiguracaoFranquia(props.franquiaId);

    if (config.capacidade_producao_diaria) localFormData.value.capacidade_producao_diaria = config.capacidade_producao_diaria;
    if (config.meta_producao_itens_dia) localFormData.value.meta_producao_itens_dia = config.meta_producao_itens_dia;
    if (config.valor_medio_item) localFormData.value.valor_medio_item = config.valor_medio_item;
  } catch (err) {
    // Se não houver configuração, usar valores padrão
    log('FranquiasOperacionalTab', 'loadConfiguracao', 'Erro ao carregar configuração', err);
  }
};

// Handle input change
const handleInputChange = (): void => {
  // Emitir evento se necessário
};

// Salvar
const handleSave = async (): Promise<void> => {
  isSaving.value = true;

  try {
    const operacionalPayload: Record<string, unknown> = { ...localFormData.value };
    Object.keys(operacionalPayload).forEach((key) => {
      if (operacionalPayload[key] === null) delete operacionalPayload[key];
    });

    await saveOperacional(props.franquiaId, operacionalPayload as any);

    eventBus.emit('globalMessage.success', t('Configurações salvas com sucesso'));
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao salvar configurações'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  void loadConfiguracao();
});
</script>

<style scoped>
.franquias-operacional-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Main Card */
.main-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--surface-border);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, color-mix(in srgb, var(--primary-color) 80%, white) 100%);
  border-radius: 12px;
  flex-shrink: 0;
}

.header-icon i {
  font-size: 1.25rem;
  color: white;
}

.header-content h3 {
  margin: 0 0 4px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.header-content p {
  margin: 0;
  font-size: 0.875rem;
  color: #555555;
}

/* Operacional Grid */
.operacional-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.operational-card {
  background: var(--surface-ground);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--surface-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;
}

.operational-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.operational-card.full-width {
  grid-column: 1 / -1;
}

.operational-card.primary {
  border-left: 3px solid #3b82f6;
}

.operational-card.success {
  border-left: 3px solid #22c55e;
}

.operational-card.warning {
  border-left: 3px solid #f59e0b;
}

.card-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-card);
  border-radius: 8px;
}

.card-icon i {
  font-size: 0.875rem;
  color: var(--primary-color);
}

.card-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #555555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-input {
  flex: 1;
}

.card-input :deep(.p-inputtext),
.card-input :deep(.input-currency) {
  width: 100%;
}

/* Estimate Card */
.estimate-card {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.02) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
  padding: 20px 24px;
}

.estimate-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.estimate-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.15);
  border-radius: 12px;
  flex-shrink: 0;
}

.estimate-icon i {
  font-size: 1.25rem;
  color: #22c55e;
}

.estimate-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.estimate-label {
  font-size: 0.875rem;
  color: #555555;
}

.estimate-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #22c55e;
}

.estimate-hint small {
  font-size: 0.75rem;
  color: #666666;
}

/* Progress Card */
.progress-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--surface-border);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-title i {
  color: var(--primary-color);
  font-size: 1rem;
}

.progress-title span {
  font-size: 0.938rem;
  font-weight: 500;
  color: var(--text-color);
}

.progress-percent {
  font-weight: 700;
  color: var(--primary-color);
  font-size: 1rem;
}

.progress-bar {
  height: 8px;
  background: var(--surface-ground);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #22c55e 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-hint small {
  font-size: 0.75rem;
  color: #666666;
}

/* Actions */
.actions-section {
  display: flex;
  justify-content: flex-start;
}

.actions-section :deep(.p-button) {
  padding: 12px 32px;
  font-weight: 600;
  border-radius: 8px;
}

/* Info Card */
.info-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(59, 130, 246, 0.02) 100%);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 12px;
}

.info-card i {
  color: var(--primary-color);
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-content strong {
  display: block;
  margin-bottom: 6px;
  font-size: 0.938rem;
  color: var(--text-color);
}

.info-content p {
  margin: 0;
  font-size: 0.875rem;
  color: #333333;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 768px) {
  .operacional-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .estimate-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .estimate-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
